<template>
  <div
    v-if="display"
    class="gift-with-purchase-trigger critical-component-clear"
    :class="{ 'gift-with-purchase-trigger--cart': cart }"
  >
    <transition name="fade">
      <div
        class="gift-with-purchase-trigger__progress"
      >
        <div
          class="gift-with-purchase-trigger__progress-bar"
          :style="{ width: `${progressToLargest * 100}%` }"
        />

        <div
          v-for="(step, index) of steps"
          :key="`step-${index}`"
          class="gift-with-purchase-trigger__step"
          :class="{ 'gift-with-purchase-trigger__step--complete': step.complete }"
          :style="{ left: `${step.position * 100}%` }"
        />
      </div>
    </transition>

    <p
      v-if="messageDisplay"
      class="
        gift-with-purchase-trigger__message
        text-body-l-bold
      "
    >
      <span
        class="gift-with-purchase-trigger__text"
        v-html="messageString"
      />

      <a
        v-if="progressDisplay && cta.url && cta.text"
        class="text-body-l-underline text-link"
        :href="cta.url"
        v-text="cta.text"
      />
    </p>

    <btn
      v-if="cart && availablePromotions.length"
      class="gift-with-purchase-trigger__gifts-available"
      :label="giftsAvailableText"
      class-label="text-body-l-bold"
      modifiers="text"
      :show-icon="false"
      type="button"
      @click="handleTrigger"
    />
  </div>
</template>

<script>

/**
 * Vue: Gift with purchase trigger (gift-with-purchase-trigger)
 * -----------------------------------------------------------------------------
 * Dynamic text and trigger to show modal.
 * - Displayed on PDP products that have associated gift and in the cart drawer
 *   and on the cart page.
 * - PDP displays gift message.
 * - Cart displays progress text, available gifts trigger, and progress bar.
 * - Cart progress text updates to all gifts added when thresholds met and all
 *   available gifts have been added.
 *
 * @param {Boolean} [cart] - Being rendered in the cart.
 * @param {Object} [product] - Product object if being rendered on product page.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'

export default {
  name: 'GiftWithPurchaseTrigger',

  components: {
    Btn,
  },

  props: {
    cart: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getApplicableCart: 'gift-with-purchase/getApplicableCart',
      getProductsWithGift: 'gift-with-purchase/getProductsWithGift',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      addedPromotions: (state) => state['gift-with-purchase'].added,
      availablePromotions: (state) => state['gift-with-purchase'].available,
      enablePromotions: (state) => state['gift-with-purchase'].settings.enable,
      minimumPrice: (state) => state['gift-with-purchase'].settings.minimumPrice,
      promotions: (state) => state['gift-with-purchase'].promotions,
      cta: (state) => state['gift-with-purchase'].settings.cta,
    }),

    /**
     * Compute if the trigger should be displayed.
     * @returns {Boolean}
     */
    display() {
      if (!this.enablePromotions) {
        return false
      }

      if (this.product) {
        return Boolean(this.getProductsWithGift[this.product.handle])
      }

      return Boolean(this.thresholds.length || this.availablePromotions.length)
    },

    /**
     * Compute gifts available text.
     * @returns {String}
     */
    giftsAvailableText() {
      const free = this.isPromotionFree()

      const config = {
        pluralise: this.availablePromotions.length,
        replace: {
          quantity: this.availablePromotions.length,
        },
      }

      const text = free
        ? this.$string('gift_with_purchase.product_card.free', config)
        : this.$string('gift_with_purchase.title.gift', config)

      return `${text}&nbsp;<span class="text-body-l-underline text-link">${this.$string('gift_with_purchase.view_options')}</span>`
    },

    /**
     * Compute largest threshold.
     * @returns {Object}
     */
    largestThreshold() {
      return [...this.thresholds].reverse()[0]
    },

    /**
     * Compute whether to display the message.
     * @returns {Boolean}
     */
    messageDisplay() {
      return (
        this.messageString &&
        (this.product ||
          this.progressToLargest < 1 ||
          (this.progressToLargest === 1 && !this.availablePromotions.length))
      )
    },

    /**
     * Compute message string.
     * @returns {String}
     */
    messageString() {
      if (this.product) {
        const handle = this.getProductsWithGift[this.product.handle]

        if (handle) {
          return handle.isFree
            ? this.$string('gift_with_purchase.product.free')
            : this.$string('gift_with_purchase.product.cost')
        }

        return ''
      }

      /**
       * If no thresholds.
       */
      if (!this.thresholds.length) {
        return ''
      }

      /**
       * If all thresholds met and all gifts added.
       */
      if (this.progressToLargest === 1 && !this.availablePromotions.length) {
        const free = this.addedPromotions.every((promotion) => {
          return promotion.action.discount === 100 && !this.minimumPrice
        })

        return free
          ? this.$string('gift_with_purchase.complete.free')
          : this.$string('gift_with_purchase.complete.cost')
      }

      /**
       * Determine cost to next threshold and whether the gift is free.
       */
      const costToNextThreshold = this.nextThreshold
        ? this.nextThreshold?.trigger?.value[cnvs.store.currency] -
          this.getApplicableCart.total_price
        : 0

      const nextFree =
        this.nextThreshold?.action?.discount === 100 &&
        !this.minimumPrice

      /**
       * Determine if before first threshold.
       */
      const nextThresholdIndex = this.thresholds.findIndex((threshold) => {
        return threshold === this.nextThreshold
      })

      const nextThresholdValue = this.nextThreshold?.trigger?.value[cnvs.store.currency]
      let threshold = nextThresholdValue

      /**
       * If not first threshold then get previous threshold to determine
       * difference for progress text.
       */
      if (nextThresholdIndex > 0) {
        const previousThreshold = this.thresholds[nextThresholdIndex - 1]

        threshold =
          nextThresholdValue -
          previousThreshold.trigger.value[cnvs.store.currency]

      }

      const config = {
        replace: {
          currency_symbol: cnvs.store.currencySymbol,
          remaining: this.$formatMoney(costToNextThreshold, cnvs.store.moneyFormatNoSymbol),
          threshold: this.$formatMoney(threshold, cnvs.store.moneyFormatNoSymbol),
        },
      }

      if (nextFree) {
        return nextThresholdIndex > 0
          ? this.$string('gift_with_purchase.threshold.free.multi_progress', config)
          : this.$string('gift_with_purchase.threshold.free.progress_html', config)
      }

      return nextThresholdIndex > 0
        ? this.$string('gift_with_purchase.threshold.cost.multi_progress', config)
        : this.$string('gift_with_purchase.threshold.cost.progress_html', config)
    },

    /**
     * Compute next threshold.
     * - Returns false if no thresholds remaining.
     * @returns {Object|Boolean}
     */
    nextThreshold() {
      const thresholdsNotMet = this.thresholds.filter((threshold) => {
        return threshold.trigger.value[cnvs.store.currency] > this.getApplicableCart.total_price
      })

      return thresholdsNotMet.length ? thresholdsNotMet[0] : false
    },

    /**
     * Compute whether to display progress bar.
     * @returns {Boolean}
     */
    progressDisplay() {
      return (
        this.cart &&
        this.thresholds.length &&
        (this.availablePromotions.length || this.progressToLargest < 1)
      )
    },

    /**
     * Compute progress decimal to next threshold.
     * - Can't return a value larger than 1.
     * @returns {Number}
     */
    progressToNext() {
      if (!this.nextThreshold) {
        return 0
      }

      const nextThreshold = this.nextThreshold.trigger.value[cnvs.store.currency]
      return Math.min(this.getApplicableCart.total_price / nextThreshold, 1)
    },

    /**
     * Compute progress decimal to largest threshold.
     * - Can't return a value larger than 1.
     * @returns {Number}
     */
    progressToLargest() {
      if (this.cart && this.availablePromotions.length) {
        return 1
      }

      if (!this.largestThreshold) {
        return 0
      }

      const largestThresholdValue = this.largestThreshold.trigger.value[cnvs.store.currency]
      return Math.min(this.getApplicableCart.total_price / largestThresholdValue, 1)
    },

    /**
     * Compute array of threshold steps.
     * - Ignores largest (last) threshold.
     * @returns {Array}
     */
    steps() {
      return this.thresholds.map((threshold) => {
        if (threshold.id === this.largestThreshold.id) {
          return false
        }

        return {
          complete:
            this.getApplicableCart.total_price >= threshold.trigger.value[cnvs.store.currency],
          position:
            threshold.trigger.value[cnvs.store.currency] /
            this.largestThreshold.trigger.value[cnvs.store.currency]
          ,
        }
      }).filter(Boolean)
    },

    /**
     * Compute all thresholds in order.
     * @returns {Array}
     */
    thresholds() {
      let thresholds = this.promotions.filter((promotion) => {
        return promotion.trigger.type === 'threshold'
      })

      thresholds = thresholds.sort((previous, current) => {
        return (
          previous.trigger.value[cnvs.store.currency] -
          current.trigger.value[cnvs.store.currency]
        )
      })

      return thresholds
    },
  },
  methods: {

    /**
     * Compute if all available promotions are free.
     */
    isPromotionFree() {
      return this.availablePromotions.every((promotion) => {
        const discountsArr = promotion.action.discount.split('|')

        return discountsArr.every((discount) => {
          return Number(discount) === 100
        }) && !this.minimumPrice
      })
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle trigger click.
     */
    handleTrigger() {
      if (!this.availablePromotions.length) {
        return
      }

      this.openOverlay({
        component: 'gift-with-purchase-modal',
        ignoreDismissed: true,
        namespace: 'giftWithPurchaseModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import './gift-with-purchase-trigger';
</style>
